import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bot, Plus, Save, ToggleLeft, ToggleRight } from "lucide-react";
import { API, authHeaders, PageHeader } from "@/lib/streamcareShared";

const emptyRule = { name: "", keywords: "", response_text: "", enabled: true, order: 100 };

const normalizeRuleForForm = (rule) => ({
  ...rule,
  keywords: (rule.keywords || []).join(", "),
});

const payloadFromForm = (form) => ({
  name: form.name.trim(),
  keywords: String(form.keywords || "").split(",").map((item) => item.trim()).filter(Boolean),
  response_text: form.response_text.trim(),
  enabled: Boolean(form.enabled),
  order: Number(form.order || 100),
});

export default function FAQBotPage() {
  const [enabled, setEnabled] = useState(true);
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState(emptyRule);
  const [editingId, setEditingId] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await axios.get(`${API}/faq-bot`, { headers: authHeaders() });
    setEnabled(data.enabled);
    setRules(data.rules || []);
  };

  useEffect(() => { load(); }, []);

  const toggleBot = async () => {
    const next = !enabled;
    setEnabled(next);
    await axios.patch(`${API}/faq-bot/settings`, { enabled: next }, { headers: authHeaders() });
    toast.success(next ? "Bot FAQ activé" : "Bot FAQ désactivé");
  };

  const saveRule = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = payloadFromForm(form);
      if (!payload.name || !payload.keywords.length || !payload.response_text) {
        toast.error("Nom, mots-clés et réponse sont obligatoires");
        return;
      }
      if (editingId) await axios.patch(`${API}/faq-bot/rules/${editingId}`, payload, { headers: authHeaders() });
      else await axios.post(`${API}/faq-bot/rules`, payload, { headers: authHeaders() });
      toast.success(editingId ? "Règle mise à jour" : "Règle ajoutée");
      setForm(emptyRule);
      setEditingId("");
      await load();
    } finally {
      setSaving(false);
    }
  };

  const editRule = (rule) => {
    setEditingId(rule.id);
    setForm(normalizeRuleForForm(rule));
  };

  const toggleRule = async (rule) => {
    await axios.patch(`${API}/faq-bot/rules/${rule.id}`, { enabled: !rule.enabled }, { headers: authHeaders() });
    toast.success(!rule.enabled ? "Règle activée" : "Règle désactivée");
    await load();
  };

  return (
    <section data-testid="faq-bot-page">
      <PageHeader title="FAQ Bot" subtitle="Réponses automatiques par mots-clés pour les nouveaux tickets clients." icon={Bot} action={<button className="btn-primary" onClick={toggleBot} data-testid="faq-bot-toggle-button">{enabled ? <ToggleRight size={18} /> : <ToggleLeft size={18} />} {enabled ? "Bot activé" : "Bot désactivé"}</button>} />

      <div className="content-grid" data-testid="faq-bot-content-grid">
        <form className="surface panel-body" onSubmit={saveRule} data-testid="faq-bot-rule-form">
          <div className="flex items-center gap-3" data-testid="faq-bot-form-heading"><Plus className="text-[#d4af37]" size={20} /><h2 className="font-[Outfit] text-xl text-white" data-testid="faq-bot-form-title">{editingId ? "Modifier la règle" : "Nouvelle règle"}</h2></div>
          <div className="mt-5 grid gap-3" data-testid="faq-bot-form-fields">
            <input className="field" placeholder="Nom de la règle" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} data-testid="faq-bot-rule-name-input" />
            <input className="field" placeholder="Mots-clés séparés par des virgules" value={form.keywords} onChange={(event) => setForm({ ...form, keywords: event.target.value })} data-testid="faq-bot-rule-keywords-input" />
            <textarea className="field min-h-[220px] pt-3" placeholder="Réponse automatique" value={form.response_text} onChange={(event) => setForm({ ...form, response_text: event.target.value })} data-testid="faq-bot-rule-response-textarea" />
            <div className="grid gap-3 sm:grid-cols-2" data-testid="faq-bot-form-meta-row">
              <input className="field" type="number" placeholder="Ordre" value={form.order} onChange={(event) => setForm({ ...form, order: event.target.value })} data-testid="faq-bot-rule-order-input" />
              <select className="field" value={form.enabled ? "true" : "false"} onChange={(event) => setForm({ ...form, enabled: event.target.value === "true" })} data-testid="faq-bot-rule-enabled-select"><option value="true">Règle active</option><option value="false">Règle désactivée</option></select>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2" data-testid="faq-bot-form-actions">
            <button className="btn-primary" disabled={saving} data-testid="faq-bot-rule-save-button"><Save size={16} /> {saving ? "Enregistrement…" : "Enregistrer"}</button>
            {editingId && <button type="button" className="btn-secondary" onClick={() => { setEditingId(""); setForm(emptyRule); }} data-testid="faq-bot-rule-cancel-button">Annuler</button>}
          </div>
        </form>

        <section className="surface" data-testid="faq-bot-rules-list">
          <div className="panel-header" data-testid="faq-bot-rules-header"><h2 className="font-[Outfit] text-xl text-white" data-testid="faq-bot-rules-title">Règles automatiques</h2><span className="badge badge-gold" data-testid="faq-bot-rules-count">{rules.length} règle{rules.length > 1 ? "s" : ""}</span></div>
          <div className="list-stack p-4" data-testid="faq-bot-rule-cards">
            {rules.map((rule) => <article key={rule.id} className="faq-bot-rule-card" data-testid={`faq-bot-rule-card-${rule.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3" data-testid={`faq-bot-rule-header-${rule.id}`}><div><h3 className="font-[Outfit] text-lg text-white" data-testid={`faq-bot-rule-name-${rule.id}`}>{rule.name}</h3><p className="mt-1 text-xs text-slate-500" data-testid={`faq-bot-rule-order-${rule.id}`}>Ordre {rule.order}</p></div><span className={rule.enabled ? "badge badge-green" : "badge badge-neutral"} data-testid={`faq-bot-rule-status-${rule.id}`}>{rule.enabled ? "Active" : "Désactivée"}</span></div>
              <div className="mt-3 flex flex-wrap gap-2" data-testid={`faq-bot-rule-keywords-${rule.id}`}>{(rule.keywords || []).map((keyword) => <span className="faq-keyword-chip" key={keyword}>{keyword}</span>)}</div>
              <p className="mt-3 whitespace-pre-line text-sm text-slate-300" data-testid={`faq-bot-rule-response-${rule.id}`}>{rule.response_text}</p>
              <div className="mt-4 flex flex-wrap gap-2" data-testid={`faq-bot-rule-actions-${rule.id}`}><button className="btn-secondary" onClick={() => editRule(rule)} data-testid={`faq-bot-rule-edit-${rule.id}`}>Modifier</button><button className="btn-secondary" onClick={() => toggleRule(rule)} data-testid={`faq-bot-rule-toggle-${rule.id}`}>{rule.enabled ? "Désactiver" : "Activer"}</button></div>
            </article>)}
          </div>
        </section>
      </div>
    </section>
  );
}